import { FC, useCallback, type SyntheticEvent } from 'react';
import { useTranslation } from 'next-i18next';
import classNames from 'classnames';
import { useRouter } from 'next/router';

import LazyLightbox from 'src/common/components/Lightbox/LazyLightbox';
import getClassNames from 'src/common/utils/getClassNames';
import { usePreviewModeContext } from 'src/common/providers/PreviewModeProvider';
import useMobileNativeWebView from 'src/common/hooks/useMobileNativeWebView';
import { buildImageSrc } from 'src/common/utils/imageHelpers';
import usePhotoLightbox from 'src/common/hooks/usePhotoLightbox';
import { GALLERY_VIEW } from 'src/listing/constants';
import type { Photo } from 'src/common/types/api/v4/boats/search';
import trackListingPhotoClick from 'src/common/utils/trackListingPhotoClick';
import HeroImage from './heroImage';
import styles from './HeroImages.module.scss';

const MAX_NUMBER_OF_IMAGES = 4;
// Threshold for enabling the full-page gallery view instead of the in-page lightbox.
const GALLERY_VIEW_PHOTOS_THRESHOLD = 5;

const bgImageParams = {
  q: 50, // reduced quality for blurred image
  w: 716,
  h: 448,
  blur: 200,
  px: 16,
};

export type HeroImagesProps = {
  headline: string;
  listingId: string;
  photos: Photo[];
  showOnePreviewImage?: boolean;
};

const HeroImages: FC<HeroImagesProps> = ({
  headline,
  listingId,
  photos,
  showOnePreviewImage = false,
}) => {
  const { isMobileNativeWebView } = useMobileNativeWebView();
  const { previewMode } = usePreviewModeContext();
  const [t] = useTranslation('common');
  // Enable full-page gallery view when number of images exceeds threshold
  // Otherwise fall back to initial lightbox
  const isGalleryViewEnabled = photos.length > GALLERY_VIEW_PHOTOS_THRESHOLD;
  const router = useRouter();
  const {
    lightboxOpen,
    lightboxStartIndex,
    lightboxImages: images,
    openLightbox,
    closeLightbox,
  } = usePhotoLightbox({
    photos,
    headline,
  });

  const showPlaceholder = previewMode && photos.length === 0;
  const displayImages = photos.slice(0, MAX_NUMBER_OF_IMAGES);
  const placeholderDisplayImages = [{ id: 1 }, { id: 2 }, { id: 3 }];

  const onImageClick = useCallback((index: number) => (
    event: SyntheticEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    trackListingPhotoClick({
      index,
      imageLocation: 'hero',
      listingId,
      totalPhotos: photos.length,
    });
    if (!isGalleryViewEnabled) {
      openLightbox(event, index);
    }
  }, [isGalleryViewEnabled, openLightbox, photos.length, listingId]);

  if (!previewMode && displayImages.length === 0) {
    return null;
  }

  const backgroundImage = photos.length > 0
    ? `url(${buildImageSrc(photos[0].url, bgImageParams)})`
    : undefined;

  let modifier = showPlaceholder
    ? `${placeholderDisplayImages.length}-images`
    : `${displayImages.length}-images`;

  if (showOnePreviewImage && !showPlaceholder) {
    modifier = '1-images';
  }

  return (
    <div className={getClassNames(styles, 'root', classNames(modifier, { showOnePreviewImage }))}>
      <div
        className={getClassNames(styles, 'background', classNames(modifier, { placeholder: showPlaceholder }))}
        style={{ backgroundImage }}
      />
      <div className={getClassNames(styles, 'images', classNames(modifier, { showOnePreviewImage, isMobileNativeWebView }))}>
        {!showPlaceholder && displayImages.map((displayImage, index) => (
          <HeroImage
            key={displayImage.url}
            img={displayImage}
            href={`#${GALLERY_VIEW}/${index}`}
            className={getClassNames(styles, 'imageContainer', modifier)}
            large={
              index === 0 // first image is always large
              || index === 3 // so is the last
              || displayImages.length === 2 // in this case both will be large
            }
            headline={headline}
            loading={index === 0 ? 'eager' : 'lazy'}
            onClick={onImageClick(index)}
            isGalleryViewEnabled={isGalleryViewEnabled}
          />
        ))}
        {showPlaceholder && placeholderDisplayImages.map(({ id }) => (
          <div
            key={id}
            className={getClassNames(
              styles,
              'imageContainer',
              classNames(modifier, { placeholderImage: showPlaceholder }),
            )}
          />
        ))}
      </div>
      {(showPlaceholder || photos.length > 1) && (
        <div className={styles.buttonContainer}>
          <button
            type="button"
            onClick={isGalleryViewEnabled
              ? () => router.push(`#${GALLERY_VIEW}`)
              : openLightbox}
            className={getClassNames(
              styles,
              'button',
              classNames({ placeholder: showPlaceholder }),
            )}
          >
            {showPlaceholder
              ? t('photos--placeholder')
              : t('hero-images--cta')}
          </button>
        </div>
      )}

      <LazyLightbox
        label={headline}
        images={images}
        isOpen={lightboxOpen}
        startIndex={lightboxStartIndex}
        closeModal={closeLightbox}
        enableSideClickNavigation
      />
    </div>
  );
};

export default HeroImages;
